import { BasicResponseDTO } from '@dto/basicResponse.dto';
import { Group, GroupsResponseDTO } from '@dto/groupsResponse.dto';
import { UserDTO, UsersResponseDTO } from '@dto/usersResponse.dto';
import axios, { AxiosError } from 'axios';
import { Log } from 'src/service/log.service';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface UsersGroupsStoreState {
  // State
  usersData: UserDTO[];
  groupData: Group[];
  loadingUserGroupData: boolean;
}

interface UsersGroupsStoreActions {
  // Actions
  getDataUsersGroups: () => Promise<void>;
  sortGroupsByName: (groupIds: string[]) => string[];
}

export const USERS_GROUPS_INITIAL_STATE: UsersGroupsStoreState = {
  usersData: [],
  groupData: [],
  loadingUserGroupData: false,
};

export const URL_USER_MANAGEMENT = {
  USERS: '/api/users/v1/users/',
  GROUPS: '/api/users/v1/groups/',
} as const;

export const useUsersGroupsStore = create<UsersGroupsStoreState & UsersGroupsStoreActions>()(
  devtools((set, get) => ({
    // State
    ...USERS_GROUPS_INITIAL_STATE,
    // Actions
    getDataUsersGroups: async () => {
      set({ loadingUserGroupData: true });
      try {
        const [responseUsers, responseGroups] = await Promise.all([
          await axios.get<UsersResponseDTO>(URL_USER_MANAGEMENT.USERS),
          await axios.get<GroupsResponseDTO>(URL_USER_MANAGEMENT.GROUPS),
        ]);
        const usersData = responseUsers.data?.data?.sort((a, b) =>
          a.username.localeCompare(b.username)
        );
        const groupData = responseGroups.data?.data?.sort((a, b) => a.name.localeCompare(b.name));
        set({
          usersData,
          groupData,
        });
      } catch (error) {
        const axiosError = error as AxiosError<BasicResponseDTO>;
        Log.error(axiosError.response.data.message, axiosError);
      } finally {
        set({ loadingUserGroupData: false });
      }
    },
    sortGroupsByName: (groupIds) => {
      const { groupData } = get();

      const idToNameMap = groupData?.reduce((objOfItems: Record<number, string>, obj) => {
        objOfItems[obj.group_id] = obj.name;
        return objOfItems;
      }, {});

      const sortedIds = [...groupIds];
      sortedIds?.sort((a, b) => {
        const nameA = idToNameMap[parseInt(a)];
        const nameB = idToNameMap[parseInt(b)];
        return nameA?.localeCompare(nameB);
      });

      return sortedIds;
    },
  }))
);
